import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useFetchWithAuth } from '../session/useFetchWithAuth'

const URL = import.meta.env.VITE_EPC_API_URL ?? ''

// Common success/error handler setup
const createMutation = (
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<any>,
  endpoint: string,
  successMessage: string
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetchWithAuth(`${URL}/profile/${endpoint}/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      return response
    },
    onError: (error: Error) => {
      console.error(`${endpoint} error:`, error)
      toast.error(error.message || 'An error occurred')
    },
    onSuccess: (data, id) => {
      toast.success(successMessage)
      queryClient.invalidateQueries({ queryKey: ['profile', id] })
      queryClient.invalidateQueries({ queryKey: ['adminProfileSearch'] })
    },
  })
}

export const useApproveProfileMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  return createMutation(
    fetchWithAuth,
    'approve',
    'Profile approved successfully'
  )
}

export const useSuspendProfileMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  return createMutation(
    fetchWithAuth,
    'suspend',
    'Profile suspended successfully'
  )
}

export const useActivateProfileMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  return createMutation(
    fetchWithAuth,
    'activate',
    'Profile activated successfully'
  )
}

export const useDeactivateProfileMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  return createMutation(
    fetchWithAuth,
    'deactivate',
    'Profile deactivated successfully'
  )
}

export const useDenyProfileMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      reason,
      message,
    }: {
      id: string
      reason: string
      message: string
    }) => {
      const response = await fetchWithAuth(`${URL}/profile/deny/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, message }),
      })
      return response
    },
    onError: (error: Error) => {
      console.error('Deny profile error:', error)
      toast.error(error.message || 'An error occurred')
    },
    onSuccess: (data, variables) => {
      toast.success('Profile denied successfully')
      queryClient.invalidateQueries({ queryKey: ['profile', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['adminProfileSearch'] })
    },
  })
}

export const useDestroyProfileMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetchWithAuth(`${URL}/profile/destroy/${id}`, {
        method: 'DELETE',
      })
      return response
    },
    // Optimistically remove profile from cache before request completes
    onMutate: async (profileId: string) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ['adminProfileSearch'] })

      // Snapshot the previous value for rollback
      const previousSearchData = queryClient.getQueriesData({
        queryKey: ['adminProfileSearch'],
      })

      // Optimistically update all search caches to remove the deleted profile
      queryClient.setQueriesData(
        { queryKey: ['adminProfileSearch'] },
        (old: any) => {
          if (!old?.profiles) return old
          return {
            ...old,
            profiles: old.profiles.filter((p: any) => p.id !== profileId),
            totalCount: Math.max(0, (old.totalCount || 0) - 1),
          }
        }
      )

      // Return context for rollback on error
      return { previousSearchData }
    },
    onError: (error: Error, profileId, context) => {
      // Rollback optimistic update on error
      if (context?.previousSearchData) {
        context.previousSearchData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }

      console.error('Destroy profile error:', error)
      toast.error(error.message || 'An error occurred')
      return { success: false, errors: [{ message: error.message }] }
    },
    onSuccess: (data, profileId) => {
      if (data.success) {
        toast.success('Profile deleted successfully')

        // Invalidate and force immediate refetch of all search queries
        queryClient.invalidateQueries({
          queryKey: ['adminProfileSearch'],
          refetchType: 'active', // Force immediate refetch of mounted queries
        })

        // Also invalidate the specific profile query in case it's cached
        queryClient.invalidateQueries({
          queryKey: ['profile', profileId],
        })

        // Show warning if Algolia sync failed
        if (data.algolia_synced === false) {
          toast.warning(
            'Profile deleted but search index update delayed. ' +
              'Search results may take a few minutes to update.',
            { duration: 5000 }
          )
        }

        return { success: true }
      } else {
        toast.error(data.errors?.[0]?.message || 'Failed to delete profile')
        return { success: false, errors: data.errors }
      }
    },
  })
}
